import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import StatCard from '../../components/ui/StatCard';
import Card from '../../components/ui/Card';
import { useAuth } from '../../contexts/AuthContext';

import { subscribeUserSimulations } from '../../services/simulacaoService';
import { subscribePdfs } from '../../services/PdfService';
import { useUserProfile } from '../../contexts/UserContext';
import { subscribeClientsByRole } from '../../services/clientService';
import { subscribeSimulationsByRole } from '../../services/simulacaoService';
import { subscribePdfsByRole } from '../../services/PdfService';

const AVATAR_COLORS = ['#3B82F6', '#8B5CF6', '#14B8A6', '#F43F5E', '#F97316', '#EC4899', '#6366F1', '#0EA5E9'];

function getColor(name) {
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getIniciais(name) {
  return (name || 'U').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
}

function formatDate(timestamp) {
  if (!timestamp) return '—';
  const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diff = now - d;
  if (diff < 86400000) return `Hoje, ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  if (diff < 172800000) return `Ontem, ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  return d.toLocaleDateString('pt-BR');
}

function parseValor(str) {
  if (!str) return 0;
  const raw = String(str).replace(/[^\d,]/g, '').replace(',', '.');
  return parseFloat(raw) || 0;
}

function formatVolume(valor) {
  if (valor >= 1_000_000) return `R$ ${(valor / 1_000_000).toFixed(1).replace('.', ',')}M`;
  if (valor >= 1_000) return `R$ ${(valor / 1_000).toFixed(0)}K`;
  return `R$ ${valor.toFixed(0)}`;
}

// ======== STATUS MAP ========
const STATUS_MAP = {
  novo: { label: 'Novo Lead', background: 'rgba(59,130,246,0.1)', color: '#3B82F6' },
  simulacao: { label: 'PDF Enviado', background: 'rgba(59,130,246,0.1)', color: '#3B82F6' },
  analise: { label: 'Aguardando', background: 'rgba(245,166,35,0.1)', color: '#D97706' },
  fechado: { label: 'Contrato Fechado', background: 'rgba(0,214,143,0.1)', color: '#059669' },
  perdido: { label: 'Perdido', background: 'rgba(244,63,94,0.1)', color: '#F43F5E' },
};

// ======== CORES OFICIAIS DOS BANCOS ========
const BANCO_CORES = {
  'Inter': '#FF500F',
  'Caixa': '#005CA9',
  'BRB': '#003882',
  'Bradesco': '#CC092F',
  'Santander': '#EC0000',
  'Itaú': '#FF6600',
  'Banco do Brasil': '#FECE00',
  'Sicoob': '#003641',
  'Sicredi': '#00A651',
};

function getBancoCor(nome, corOriginal) {
  if (corOriginal && corOriginal !== '#666') return corOriginal;
  const key = Object.keys(BANCO_CORES).find((k) => nome?.toLowerCase().includes(k.toLowerCase()));
  return key ? BANCO_CORES[key] : '#666';
}

// ======== STAGES CONFIG ========
const STAGES = [
  { key: 'novo', label: 'Novo Lead', cor: '#3B82F6' },
  { key: 'simulacao', label: 'Simulação Enviada', cor: '#8B5CF6' },
  { key: 'analise', label: 'Em Análise', cor: '#F5A623' },
  { key: 'fechado', label: 'Contrato Fechado', cor: '#00D68F' },
  { key: 'perdido', label: 'Perdido', cor: '#F43F5E' },
];

// ======== BAR CHART — FASES DO PIPELINE ========
function PipelineBarChart({ clients }) {
  const counts = {};
  STAGES.forEach((s) => { counts[s.key] = 0; });
  clients.forEach((c) => {
    const stage = c.stage || 'novo';
    if (counts[stage] !== undefined) counts[stage]++;
    else counts['novo']++;
  });

  const maxCount = Math.max(...Object.values(counts), 1);

  return (
    <Card style={{ padding: 20 }}>
      <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
        📊 Qtd Clientes por Fase
      </div>
      {clients.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', textAlign: 'center', padding: 30 }}>
          Dados aparecerão após cadastrar clientes
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, height: 160, paddingTop: 10 }}>
          {STAGES.map((s) => {
            const count = counts[s.key];
            const pct = (count / maxCount) * 100;
            return (
              <div key={s.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, height: '100%', justifyContent: 'flex-end' }}>
                <div style={{
                  width: '100%', height: `${Math.max(pct, 4)}%`, background: s.cor,
                  borderRadius: '6px 6px 2px 2px', minHeight: count > 0 ? 20 : 4, position: 'relative',
                  transition: 'height 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
                }}>
                  {count > 0 && (
                    <span style={{
                      position: 'absolute', top: -18, left: '50%', transform: 'translateX(-50%)',
                      fontSize: '0.65rem', fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap',
                    }}>{count}</span>
                  )}
                </div>
                <span style={{ fontSize: '0.58rem', color: 'var(--text-muted)', fontWeight: 600, textAlign: 'center', lineHeight: 1.2 }}>
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ======== DONUT — SIMULAÇÕES POR BANCO ========
function BankDonutChart({ simulations }) {
  const bancoCount = {};
  simulations.forEach((sim) => {
    (sim.bancos || []).forEach((b) => {
      const cor = getBancoCor(b.nome, b.cor);
      if (!bancoCount[b.nome]) bancoCount[b.nome] = { nome: b.nome, cor, count: 0 };
      bancoCount[b.nome].count++;
    });
  });

  const dados = Object.values(bancoCount).sort((a, b) => b.count - a.count);
  const total = dados.reduce((s, d) => s + d.count, 0);

  if (dados.length === 0) return (
    <Card style={{ padding: 20 }}>
      <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 16 }}>🏦 Qtd Simulações por Banco</div>
      <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', textAlign: 'center', padding: 30 }}>
        Dados aparecerão após simulações salvas
      </div>
    </Card>
  );

  let offset = 25;
  const segments = dados.map((d) => {
    const pct = total > 0 ? (d.count / total) * 100 : 0;
    const seg = { ...d, pct, dasharray: `${pct} ${100 - pct}`, dashoffset: offset };
    offset -= pct;
    return seg;
  });

  return (
    <Card style={{ padding: 20 }}>
      <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
        🏦 Qtd Simulações por Banco
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
        <div style={{ position: 'relative', width: 140, height: 140, flexShrink: 0 }}>
          <svg viewBox="0 0 42 42" style={{ width: 140, height: 140 }}>
            <circle cx="21" cy="21" r="15.9" fill="none" stroke="#EDEEF2" strokeWidth="5" />
            {segments.map((item) => (
              <circle
                key={item.nome}
                cx="21" cy="21" r="15.9"
                fill="none"
                stroke={item.cor}
                strokeWidth="5"
                strokeDasharray={item.dasharray}
                strokeDashoffset={item.dashoffset}
                strokeLinecap="round"
              />
            ))}
          </svg>
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontSize: '1.1rem', fontWeight: 700 }}>{total}</span>
            <span style={{ fontSize: '0.55rem', color: 'var(--text-muted)' }}>total</span>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {segments.map((item) => (
            <div key={item.nome} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.78rem' }}>
              <div style={{ width: 10, height: 10, borderRadius: 3, background: item.cor, flexShrink: 0 }} />
              <span style={{ flex: 1, whiteSpace: 'nowrap' }}>{item.nome}</span>
              <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: item.cor }}>
                {item.count}
              </span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

// ======== RECENT SIMULATIONS ========
function RecentSimulationsReal({ simulations, clients }) {
  const navigate = useNavigate();
  const recent = simulations.slice(0, 5);

  const getClientName = (clientId) => {
    if (!clientId) return 'Simulação avulsa';
    const c = clients.find((cl) => cl.id === clientId);
    return c?.nome || 'Cliente removido';
  };

  const getClientStage = (clientId) => {
    if (!clientId) return 'simulacao';
    const c = clients.find((cl) => cl.id === clientId);
    return c?.stage || 'novo';
  };

  return (
    <Card style={{ padding: 20 }}>
      <div style={{
        fontSize: '0.88rem', fontWeight: 700, marginBottom: 16,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span>Simulações Recentes</span>
        <a href="/simulacoes" onClick={(e) => { e.preventDefault(); navigate('/simulacoes'); }}
          style={{ fontSize: '0.78rem', color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 }}>
          Ver todas →
        </a>
      </div>

      {recent.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', textAlign: 'center', padding: 30 }}>
          Nenhuma simulação salva ainda
        </div>
      ) : (
        <div className="dash-table-wrap">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Cliente', 'Valor Financiado', 'Prazo', 'Melhor Banco', 'Status', 'Data'].map((h) => (
                <th key={h} style={{
                  textAlign: 'left', fontSize: '0.68rem', fontWeight: 600,
                  color: 'var(--text-muted)', textTransform: 'uppercase',
                  letterSpacing: '0.05em', padding: '8px 12px',
                  borderBottom: '1px solid var(--border)',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {recent.map((sim) => {
              const clientName = getClientName(sim.clientId);
              const cor = getColor(clientName);
              const iniciais = getIniciais(clientName);
              const melhor = sim.melhorBanco;
              const stage = getClientStage(sim.clientId);
              const status = STATUS_MAP[stage] || STATUS_MAP.novo;

              return (
                <tr key={sim.id} style={{ cursor: 'pointer' }}>
                  <td style={{ padding: 12, fontSize: '0.82rem', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: 6,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.6rem', fontWeight: 700, color: '#fff',
                        background: cor, flexShrink: 0,
                      }}>{iniciais}</div>
                      {clientName}
                    </div>
                  </td>
                  <td style={{ padding: 12, fontSize: '0.82rem', borderBottom: '1px solid var(--border)', fontVariantNumeric: 'tabular-nums' }}>
                    {sim.valorFinanciado || '—'}
                  </td>
                  <td style={{ padding: 12, fontSize: '0.82rem', borderBottom: '1px solid var(--border)' }}>
                    {sim.prazo ? `${sim.prazo} meses` : '—'}
                  </td>
                  <td style={{ padding: 12, borderBottom: '1px solid var(--border)' }}>
                    {melhor ? (
                      <span style={{
                        fontSize: '0.68rem', fontWeight: 600, padding: '3px 8px',
                        borderRadius: 100, color: '#fff', background: melhor.cor || '#666',
                      }}>{melhor.nome}</span>
                    ) : '—'}
                  </td>
                  <td style={{ padding: 12, borderBottom: '1px solid var(--border)' }}>
                    <span style={{
                      fontSize: '0.68rem', fontWeight: 600, padding: '3px 8px',
                      borderRadius: 100, background: status.background, color: status.color,
                    }}>{status.label}</span>
                  </td>
                  <td style={{ padding: 12, fontSize: '0.78rem', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>
                    {formatDate(sim.criadoEm)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}
    </Card>
  );
}

// ======== BAR CHART — VOLUME POR FASE ========
function PipelineVolumeChart({ clients }) {
  const volumes = {};
  const counts = {};
  STAGES.forEach((s) => { volumes[s.key] = 0; counts[s.key] = 0; });
  clients.forEach((c) => {
    const stage = c.stage || 'novo';
    const val = parseValor(c.valorFinanciado);
    if (volumes[stage] !== undefined) { volumes[stage] += val; counts[stage]++; }
    else { volumes['novo'] += val; counts['novo']++; }
  });

  const totalVol = Object.values(volumes).reduce((s, v) => s + v, 0);
  // Funil: cada nível tem largura proporcional, mas decresce visualmente
  const funnelStages = STAGES.filter((s) => s.key !== 'perdido');
  const perdidoVol = volumes['perdido'];
  const maxWidth = 100;

  return (
    <Card style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ fontSize: '0.82rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
          💰 Total clientes por fase
        </div>
        {totalVol > 0 && (
          <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--accent)' }}>
            {formatVolume(totalVol)}
          </span>
        )}
      </div>
      {totalVol === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', textAlign: 'center', padding: 30 }}>
          Dados aparecerão após cadastrar clientes
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
          {funnelStages.map((s, i) => {
            const vol = volumes[s.key];
            const count = counts[s.key];
            // Largura decresce por nível do funil (100%, 82%, 64%, 46%)
            const widthPct = maxWidth - (i * 18);
            const hasValue = vol > 0;
            return (
              <div key={s.key} style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{
                  width: `${widthPct}%`,
                  padding: '10px 16px',
                  background: hasValue ? s.cor : `${s.cor}22`,
                  borderRadius: i === 0 ? '10px 10px 0 0' : i === funnelStages.length - 1 ? '0 0 10px 10px' : 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
                  borderBottom: i < funnelStages.length - 1 ? '2px solid rgba(255,255,255,0.3)' : 'none',
                  position: 'relative',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: hasValue ? '#fff' : s.cor }}>
                      {s.label}
                    </span>
                    {count > 0 && (
                      <span style={{
                        fontSize: '0.58rem', fontWeight: 600, padding: '1px 6px', borderRadius: 100,
                        background: 'rgba(255,255,255,0.25)', color: '#fff',
                      }}>{count}</span>
                    )}
                  </div>
                  <span style={{
                    fontSize: '0.78rem', fontWeight: 700,
                    color: hasValue ? '#fff' : `${s.cor}88`,
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    {hasValue ? formatVolume(vol) : '—'}
                  </span>
                </div>
                {/* Connector trapezoid */}
                {i < funnelStages.length - 1 && (
                  <div style={{
                    width: 0, height: 0,
                    borderLeft: '8px solid transparent',
                    borderRight: '8px solid transparent',
                    borderTop: `6px solid ${hasValue ? s.cor : `${s.cor}22`}`,
                  }} />
                )}
              </div>
            );
          })}
          {/* Perdido - separado */}
          {perdidoVol > 0 && (
            <div style={{
              marginTop: 12, width: '40%', padding: '6px 14px',
              background: 'rgba(244,63,94,0.08)', borderRadius: 8, border: '1px dashed #F43F5E',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span style={{ fontSize: '0.68rem', fontWeight: 600, color: '#F43F5E' }}>
                Perdido ({counts['perdido']})
              </span>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#F43F5E', fontVariantNumeric: 'tabular-nums' }}>
                {formatVolume(perdidoVol)}
              </span>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ======== STACKED BAR — VOLUME POR BANCO ========
function BankVolumeChart({ simulations }) {
  const bancoVol = {};
  simulations.forEach((sim) => {
    (sim.bancos || []).forEach((b) => {
      const cor = getBancoCor(b.nome, b.cor);
      const val = b.totalAPagar || 0;
      if (!bancoVol[b.nome]) bancoVol[b.nome] = { nome: b.nome, cor, volume: 0 };
      bancoVol[b.nome].volume += val;
    });
  });

  const dados = Object.values(bancoVol).sort((a, b) => b.volume - a.volume);
  const total = dados.reduce((s, d) => s + d.volume, 0);
  const maxVol = dados.length > 0 ? dados[0].volume : 1;

  if (dados.length === 0) return (
    <Card style={{ padding: 20 }}>
      <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 16 }}>🏦 Total Simulações por Banco</div>
      <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', textAlign: 'center', padding: 30 }}>
        Dados aparecerão após simulações salvas
      </div>
    </Card>
  );

  return (
    <Card style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ fontSize: '0.82rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
          🏦 Total Simulações por Banco
        </div>
        <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--accent)' }}>
          {formatVolume(total)}
        </span>
      </div>

      {/* Stacked bar */}
      <div style={{ height: 28, borderRadius: 8, overflow: 'hidden', display: 'flex', marginBottom: 16 }}>
        {dados.map((d) => {
          const pct = total > 0 ? (d.volume / total) * 100 : 0;
          return (
            <div key={d.nome} title={`${d.nome}: ${formatVolume(d.volume)}`} style={{
              width: `${pct}%`, height: '100%', background: d.cor,
              transition: 'width 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
              minWidth: pct > 0 ? 4 : 0,
            }} />
          );
        })}
      </div>

      {/* Legend with individual bars */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {dados.map((d, i) => {
          const pct = maxVol > 0 ? (d.volume / maxVol) * 100 : 0;
          return (
            <div key={d.nome} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{
                width: 22, height: 22, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.58rem', fontWeight: 700, color: '#fff', background: d.cor, flexShrink: 0,
              }}>{i + 1}</span>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ fontSize: '0.72rem', fontWeight: 600 }}>{d.nome}</span>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: d.cor, fontVariantNumeric: 'tabular-nums' }}>
                    {formatVolume(d.volume)}
                  </span>
                </div>
                <div style={{ height: 5, borderRadius: 3, background: 'var(--border)', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 3, width: `${pct}%`, background: d.cor,
                    transition: 'width 0.6s ease',
                  }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ======== DASHBOARD PAGE ========
export default function DashboardPage() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [simulations, setSimulations] = useState([]);
  const [pdfs, setPdfs] = useState([]);
const { profile } = useUserProfile();
  useEffect(() => {
    if (!user) return;
    const unsub1 = subscribeClientsByRole(profile, setClients);
    const unsub2 = subscribeSimulationsByRole(profile, setSimulations);
    const unsub3 = subscribePdfsByRole(profile, setPdfs);
    return () => { unsub1(); unsub2(); unsub3(); };
  }, [profile]);

  // Stats calculados
  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const simulacoesEsteMes = simulations.filter((s) => {
    if (!s.criadoEm) return false;
    const d = s.criadoEm.toDate ? s.criadoEm.toDate() : new Date(s.criadoEm);
    return d >= firstOfMonth;
  }).length;

  const pdfsGerados = pdfs.length;
  const clientesAtivos = clients.filter((c) => c.stage !== 'perdido').length;

  const volumeSimulado = simulations.reduce((sum, s) => sum + parseValor(s.valorFinanciado), 0);

  return (
    <>
      {/* Stats */}
      <div className="dash-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
        <StatCard icon="⚡" iconColor="green" label="Simulações este mês" value={simulacoesEsteMes.toString()} />
        <StatCard icon="📄" iconColor="blue" label="PDFs gerados" value={pdfsGerados.toString()} />
        <StatCard icon="👥" iconColor="purple" label="Clientes ativos" value={clientesAtivos.toString()} />
        <StatCard icon="💰" iconColor="gold" label="Volume simulado" value={formatVolume(volumeSimulado)} />
      </div>

      {/* Charts — Quantidade */}
      <div className="dash-charts" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <PipelineBarChart clients={clients} />
        <BankDonutChart simulations={simulations} />
      </div>

      {/* Charts — Volume (R$) */}
      <div className="dash-charts" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <PipelineVolumeChart clients={clients} />
        <BankVolumeChart simulations={simulations} />
      </div>

      {/* Recent */}
      <RecentSimulationsReal simulations={simulations} clients={clients} />

      <style>{`
        @media(max-width:768px){
          .dash-stats{grid-template-columns:repeat(2,1fr)!important;gap:10px!important}
          .dash-charts{grid-template-columns:1fr!important;gap:12px!important}
          .dash-table-wrap{overflow-x:auto;-webkit-overflow-scrolling:touch}
          .dash-table-wrap table{min-width:600px}
        }
        @media(max-width:480px){
          .dash-stats{grid-template-columns:repeat(2,1fr)!important}
        }
      `}</style>
    </>
  );
}